// ... [semua import tetap sama seperti sebelumnya] ...
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ChatHeader from './ChatHeader';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import FeedbackScreen from './FeedbackScreen';
import ConsultationReminderModal from "./ConsultationReminderModal";
import styled from 'styled-components';
import { zim } from '../../../zegoConfig';
import { loginToZego } from '../../../components/utils/zegoLogin';
import axios from '../../../api/axios';

import HeaderWithBack from '../../../components/HeaderWithBack';
import BottomNav from '../../../components/BottomNav';
import PatientCard from './PatientCard';
import { isAxiosError } from 'axios';
import { decryptFromBackend } from '../../../utils/urlDecryptor';
import { Data } from '@react-google-maps/api';


const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100dvh; /* Fallback: 100vh */
  height: 100vh;
  overflow: hidden;
  align-self: center;
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 120px 16px 130px;
  overflow-y: auto;
  background-image: url('/images/bg-chat.png'); /* Path ke gambar kamu */
  background-size: cover;        /* Gambar akan menutupi seluruh area */
  background-repeat: no-repeat;  /* Tidak mengulang gambar */
  background-position: center;   /* Posisikan gambar di tengah */
  
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  justify-content: center;
`;

const ChatInputContainer = styled.div`
  position: fixed;
  bottom: 0;
  bottom: env(safe-area-inset-bottom);
  background: #ffffff;
  padding: 0 0 calc(env(safe-area-inset-bottom) + 8px);
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
  z-index: 100;
  transform: translateY(0);
  transition: transform 0.2s ease;
  width: 100%;
`;


interface ReceivedMessage {
  type: number;
  messageID: string;
  timestamp: number;
  message: string;
  senderUserID: string;
  thumbnailDownloadUrl?: string;
  largeImageDownloadUrl?: string;
  fileDownloadUrl?: string;
  fileName?: string;
}

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const { teleconsultationId, token } = useParams<{
    teleconsultationId: string;
    token: string;
  }>();

  const decryptedToken = useMemo(() => {
    console.log('🔐 decryptedToken useMemo running', { token: token ? '[present]' : '[empty/undefined]' });
    if (!token) return '';
    try {
      const result = decryptFromBackend(token);
      console.log('✅ Token decrypted successfully');
      return result;
    } catch (err) {
      console.error('❌ Failed to decrypt token:', err);
      return '';
    }
  }, [token]);

  // const storedUserData = sessionStorage.getItem('userData');
  // const userData = storedUserData ? JSON.parse(storedUserData) : null;
  // const token = userData ? userData.token : null;

  const handleNavigationError = useCallback((error: any) => {
    if (isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 400)) {
      navigate('/not-found');
    } else if (error.status === 403) {
      console.log('You Are Not Authorized to access this resource!')
      navigate('/forbidden');
    } else {
      console.error('Unexpected error:', error);
    }
  }, [navigate]);

  const [teleconsultationData, setTeleconsultationData] = useState<any>(null);
  // Fetch teleconsultation data with retry capability
  const fetchTeleconsultationData = useCallback(async () => {
    console.log('🔍 fetchTeleconsultationData called', { teleconsultationId, decryptedToken: decryptedToken ? '[present]' : '[empty]' });
    // if (!teleconsultationId || !decryptedToken) {
    //   console.log('⏭️ fetchTeleconsultationData skipped - missing params', { teleconsultationId, hasToken: !!decryptedToken });
    //   return;
    // }

    setDataError(null);
    try {
      const response = await axios.get(`teleconsultation/${teleconsultationId}`, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });

      const data = response.data;

      sessionStorage.setItem('teleconsultationData', JSON.stringify(data));
      setTeleconsultationData(data);
    } catch (error) {
      console.error('Gagal mengambil data telekonsultasi:', error);
      setDataError('Gagal memuat data. Silakan coba lagi.');
      handleNavigationError(error);
    }
  }, [teleconsultationId, decryptedToken, handleNavigationError]);

  // ✅ FIX: Explicitly depend on the actual values that matter
  // This ensures the fetch is triggered when teleconsultationId or decryptedToken become available
  useEffect(() => {
    console.log('📦 useEffect triggered for fetchTeleconsultationData', { teleconsultationId, hasToken: !!decryptedToken });
    if (teleconsultationId) {
      fetchTeleconsultationData();
    }
  }, [teleconsultationId, fetchTeleconsultationData]);

  // console.log('Teleconsultation Data:', teleconsultationData);

  const userId = teleconsultationData?.patient_id ? `LMS${teleconsultationData.patient_id}${process.env.REACT_APP_ZEGOCLOUD_APP_ENV || ''}` : '';
  const username = teleconsultationData?.patient_name || 'defaultUser';
  const userDokter = teleconsultationData?.practitioner_id ? `LMS${teleconsultationData.practitioner_id}${process.env.REACT_APP_ZEGOCLOUD_APP_ENV || ''}` : '';
  const conversationType = 0;

  // ✅ FIX: Use room_id from API response for chat identification, not URL param
  const roomId = teleconsultationData?.room_id || null;

  const [messages, setMessages] = useState<any[]>([]);
  const [messageIds, setMessageIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextMessage, setNextMessage] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [highlightedMessageID, setHighlightedMessageID] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<any>(null);

  // New error states for better UX and retry capability
  const [loginError, setLoginError] = useState<string | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const [loginRetryCount, setLoginRetryCount] = useState(0);
  const MAX_LOGIN_RETRIES = 3;


  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const loginStartedRef = useRef(false);
  const chatInputRef = useRef<HTMLDivElement | null>(null);
  const messageRefsMap = useRef<{ [key: string]: HTMLDivElement | null }>({});

  //Reset Message agar state tidak nyangkut di pesan lain
  useEffect(() => {
    setMessages([]);
  }, [teleconsultationId]);

  function useKeyboardOffset() {
    const [keyboardOffset, setKeyboardOffset] = useState(0);

    useEffect(() => {
      const viewport = window.visualViewport;

      if (!viewport) {
        // visualViewport not supported (fallback)
        return;
      }

      const handleResize = () => {
        const heightDiff =
          window.innerHeight - viewport.height - viewport.offsetTop;
        setKeyboardOffset(heightDiff > 0 ? heightDiff : 0);
      };

      viewport.addEventListener("resize", handleResize);
      viewport.addEventListener("scroll", handleResize);

      // Initial check
      handleResize();

      return () => {
        viewport.removeEventListener("resize", handleResize);
        viewport.removeEventListener("scroll", handleResize);
      };
    }, []);

    return keyboardOffset;
  }

  const keyboardOffset = useKeyboardOffset();
  const handleReplyQuoteClick = (messageID: string) => {
    setHighlightedMessageID(messageID);

    const messageElement = messageRefsMap.current[messageID];
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    setTimeout(() => {
      setHighlightedMessageID(null);
    }, 2000);
  };

  // Use room_id-specific localStorage keys to prevent cross-session conflicts
  // ✅ FIX: Use roomId from API instead of conversationID from URL
  const getStorageKey = useCallback(() => {
    return `chatMessages_${teleconsultationId}`;
  }, [roomId, teleconsultationId]);

  const saveMessagesToLocalStorage = useCallback((msgs: any[]) => {
    if (!teleconsultationId) return;
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(msgs));
    } catch (e) {
      console.error('Failed to save messages to localStorage:', e);
    }
  }, [teleconsultationId, getStorageKey]);

  const loadMessagesFromLocalStorage = useCallback(() => {
    if (!teleconsultationId) return;
    try {
      const savedMessages = localStorage.getItem(getStorageKey());
      if (savedMessages) {
        const parsed = JSON.parse(savedMessages);
        setMessages(parsed);
        const ids = parsed.map((msg: any) => msg.messageID).filter(Boolean);
        setMessageIds(new Set(ids));
      }
    } catch (e) {
      console.error('Failed to load messages from localStorage:', e);
    }
  }, [teleconsultationId, getStorageKey]);

  // ZIM Login with retry logic and error handling
  const attemptZimLogin = useCallback(async () => {
    if (!userId || !username) {
      console.error('❌ userId atau username kosong!');
      setLoginError('User ID atau username tidak tersedia');
      return;
    }

    setLoginError(null);
    console.log('🔑 Memulai login ZIM untuk userId:', userId, `(attempt ${loginRetryCount + 1})`);

    try {
      const success = await loginToZego(userId, username);
      if (success) {
        setIsLoggedIn(true);
        setLoginError(null);
        setLoginRetryCount(0);
      } else {
        throw new Error('Login gagal');
      }
    } catch (error) {
      console.error('❌ ZIM login failed:', error);
      if (loginRetryCount < MAX_LOGIN_RETRIES) {
        // Auto retry with exponential backoff
        const delay = Math.pow(2, loginRetryCount) * 1000;
        console.log(`⏳ Retrying in ${delay}ms...`);
        setTimeout(() => {
          setLoginRetryCount(prev => prev + 1);
        }, delay);
      } else {
        setLoginError('Gagal terhubung ke server chat. Silakan coba lagi.');
      }
    }
  }, [userId, username, loginRetryCount]);

  useEffect(() => {
    if (!userId || !username || isLoggedIn) return;
    if (loginStartedRef.current && loginRetryCount === 0) return;

    loginStartedRef.current = true;
    attemptZimLogin();
  }, [userId, username, isLoggedIn, loginRetryCount, attemptZimLogin]);

  const scrollToBottom = useCallback(() => {
    // 1st pass — waits for DOM layout
    requestAnimationFrame(() => {
      // 2nd pass — waits for paint/composition
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      });
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, keyboardOffset]);

  const handleSend = async (message: string) => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;

    const payloadData: any = {
      message: trimmedMessage,
      type: "text",
      time: Date.now(),
    }

    if (replyTo) {
      let replyMessageContent = replyTo.message;
      if (typeof replyTo.message === 'string') {
        try {
          const parsed = JSON.parse(replyTo.message);
          replyMessageContent = parsed.message || replyTo.message;
        } catch (e) {
          // Stay with original message
        }
      }

      payloadData.user_reply_id = replyTo.senderUserID || replyTo.isDoctor ? userDokter : userId;
      payloadData.message_id_reply = replyTo.messageID;
      payloadData.type_reply = replyTo.type_reply || "text";
      payloadData.value_reply = replyMessageContent;
    }

    const newMessage = {
      type: 1,
      message: JSON.stringify(payloadData),
      extendedData: '',
    };

    const payload = {
      to_userId: userDokter,
      from_userId: userId,
      message: newMessage.message,
      type_message: newMessage.type,
      // ✅ FIX: Use roomId from API response
      roomId: roomId,
    };


    try {
      await axios.post('chat', payload, {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
      });
      setReplyTo(null);

      // ❌ Jangan langsung push ke messages di sini
      // ✅ Tunggu sampai masuk lewat receiveMessage()

    } catch (error: any) {
      console.error('Failed to send message:', error?.response?.data || error.message);
    }
  };

  const handleSendMedia = async (file: File, mediaType: 'image' | 'file' | 'audio' | 'video') => {
    let mediaMessageObj: any;
    switch (mediaType) {
      case 'image':
        mediaMessageObj = { fileLocalPath: file, type: 11 };
        break;
      case 'file':
        mediaMessageObj = { fileLocalPath: file, type: 12 };
        break;
      case 'audio':
        mediaMessageObj = { fileLocalPath: file, type: 13, audioDuration: 100 };
        break;
      case 'video':
        mediaMessageObj = { fileLocalPath: file, type: 14, videoDuration: 100 };
        break;
      default:
        return;
    }

    try {
      const res = await zim.sendMediaMessage(
        mediaMessageObj,
        userDokter,
        conversationType,
        { priority: 1 }
      );

      const msg = res.message;
      const newMsg = {
        messageID: msg.messageID,
        message: '[media]',
        type: 'media',
        timestamp: new Date(msg.timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }),
        isDoctor: false,
        imageUrl: msg.largeImageDownloadUrl || msg.fileDownloadUrl || msg.thumbnailDownloadUrl,
        fileName: msg.fileName || 'Unknown file',
      };

      // ✅ Tampilkan di UI
      setMessages((prev) => {
        const updated = [...prev, newMsg];
        saveMessagesToLocalStorage(updated);
        return updated;
      });

      // ✅ Tambahkan ke ID set
      setMessageIds((prevIds) => {
        const newSet = new Set(prevIds);
        newSet.add(msg.messageID);
        return newSet;
      });

    } catch (error) {
      console.error('Failed to send media message:', error);
    }
  };

  const messagesRef = useRef(messages);
  const messageIdsRef = useRef(messageIds);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    messageIdsRef.current = messageIds;
  }, [messageIds]);

  const receiveMessage = (zim: any, { messageList }: { messageList: ReceivedMessage[] }) => {
    console.log(`📩 receiveMessage triggered, ${messageList.length} messages, expected doctor: ${userDokter}`);
    const newMessages: any[] = [];
    const newIds = new Set(messageIdsRef.current);
    const dokterId = userDokter; // bisa fallback kalau perlu

    for (const msg of messageList) {
      console.log(`  📩 Message from: ${msg.senderUserID}, expected doctor: ${dokterId}, userId: ${userId}`);

      // ✅ FIX: Skip messages from wrong conversations
      // Only process messages from the current doctor OR the current user
      if (msg.senderUserID !== dokterId && msg.senderUserID !== userId) {
        console.log(`  ⏭️ Skipping message from wrong conversation (sender: ${msg.senderUserID})`);
        continue;
      }

      if (newIds.has(msg.messageID)) continue;
      newIds.add(msg.messageID);

      const timestamp = new Date(msg.timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      // Cek jika pesan diawali dengan "end|" dan ambil ID-nya
      // ✅ FIX: Only process "end|" messages that are less than 5 minutes old
      // This prevents old queued messages from incorrectly triggering end state on first load
      if (typeof msg.message === 'string' && msg.message.startsWith('end|')) {
        const messageAge = Date.now() - msg.timestamp;
        const FIVE_MINUTES_MS = 5 * 60 * 1000;

        // Only process recent end messages (within 5 minutes)
        if (messageAge < FIVE_MINUTES_MS && roomId) {
          const parts = msg.message.split('|');
          const endId = parts[1];
          // ✅ FIX: Use roomId from API instead of URL param
          if (Number(endId) === Number(roomId)) {
            setIsExpired(true);
            setShowRating(true);
          }
        } else {
          console.log(`⏭️ Ignoring stale "end|" message (age: ${Math.round(messageAge / 1000)}s)`);
        }
      }

      let parsedMessageData: any = null;
      try {
        parsedMessageData = JSON.parse(msg.message);
      } catch (e) {
        // Jika bukan JSON, abaikan parsing
      }

      let replyTo = null;
      if (parsedMessageData && typeof parsedMessageData === 'object') {
        if (parsedMessageData.user_reply_id) {
          replyTo = {
            messageID: parsedMessageData.message_id_reply,
            senderUserID: parsedMessageData.user_reply_id,
            message: parsedMessageData.value_reply,
            type_reply: parsedMessageData.type_reply || 'text',
            isDoctor: parsedMessageData.user_reply_id === dokterId,
          };
        }
      }

      newMessages.push(
        msg.type === 1
          ? {
            messageID: msg.messageID,
            message: msg.message,
            type: 'text',
            timestamp,
            isDoctor: msg.senderUserID === dokterId,
            replyTo: replyTo,
          }
          : {
            messageID: msg.messageID,
            message: msg.message || '[media]',
            type: 'media',
            timestamp,
            isDoctor: msg.senderUserID === dokterId,
            imageUrl: msg.largeImageDownloadUrl || msg.fileDownloadUrl || msg.thumbnailDownloadUrl,
            fileName: msg.fileName || 'Unknown file',
            replyTo: replyTo,
          }
      );
    }

    if (newMessages.length > 0) {
      const updated = [...messagesRef.current, ...newMessages];
      setMessages(updated);
      saveMessagesToLocalStorage(updated);
      setMessageIds(newIds);
    }
  };


  // Stable reference for message receiver to prevent listener leaks
  const receiveMessageRef = useRef(receiveMessage);
  useEffect(() => {
    receiveMessageRef.current = receiveMessage;
  }, [receiveMessage]);

  useEffect(() => {
    if (!isLoggedIn || !userDokter) return;

    const wrappedReceiveMessage = (zimInstance: any, data: { messageList: ReceivedMessage[] }) => {
      receiveMessageRef.current(zimInstance, data);
    };

    // Remove any existing listeners first to prevent duplicates
    try {
      zim.off('messageReceived', wrappedReceiveMessage);
      zim.off('receivePeerMessage', wrappedReceiveMessage);
    } catch (e) {
      // Ignore if listeners don't exist
    }

    zim.on('messageReceived', wrappedReceiveMessage);
    zim.on('receivePeerMessage', wrappedReceiveMessage);

    return () => {
      try {
        zim.off('messageReceived', wrappedReceiveMessage);
        zim.off('receivePeerMessage', wrappedReceiveMessage);
      } catch (e) {
        console.warn('Failed to remove ZIM listeners:', e);
      }
    };
  }, [isLoggedIn, userDokter]);

  const loadChatHistory = async (next: string | null) => {
    if (loading || !hasMore) return;
    setLoading(true);
    console.log(`📚 loadChatHistory called, querying messages with doctor: ${userDokter}`);
    try {
      // HISTORY INI AKAN MENGHASILKAN HISTORY DARI BERBAGAI USER, TIDAK HANYA DARI USER TUJUAN SAJA
      const history = await zim.queryHistoryMessage(userDokter, conversationType, {
        nextMessage: next,
        count: 100,
        reverse: true,
      });

      console.log(`📚 Retrieved ${history.messageList.length} messages from ZIM history`);

      const fetchedMessages = history.messageList;
      const newMessages: any[] = [];
      const newIds = new Set(messageIds);

      // ✅ FIX: Get consultation start time to filter messages from other consultations
      const consultationStartTime = teleconsultationData?.teleconsultation_start_time
        ? new Date(teleconsultationData.teleconsultation_start_time).getTime()
        : null;

      console.log('MADAFAKA', fetchedMessages);

      for (const msg of fetchedMessages) {
        console.log('PESAAAN', msg.message)

        if (newIds.has(msg.messageID)) continue;

        // ✅ FIX: Skip messages from wrong conversations
        // Only process messages from the current doctor OR the current user
        if (msg.senderUserID !== userDokter && msg.senderUserID !== userId) {
          console.log(`  ⏭️ [History] Skipping message from wrong conversation (sender: ${msg.senderUserID})`);
          continue;
        }

        // ✅ FIX: Skip messages from before this consultation started
        // This prevents showing messages from previous consultations with the same doctor
        // if (consultationStartTime && msg.timestamp < consultationStartTime) {
        //   console.log(`⏭️ Skipping message from before consultation started (timestamp: ${new Date(msg.timestamp).toISOString()})`);
        //   continue;
        // }

        newIds.add(msg.messageID);

        const isMedia = msg.type !== 1;
        const imageUrl = msg.largeImageDownloadUrl || msg.thumbnailDownloadUrl || msg.fileDownloadUrl;
        const fileName = msg.fileName;
        let parsedMessageData: any = null;
        try {
          parsedMessageData = JSON.parse(msg.message);
        } catch (e) {
          // Jika bukan JSON, abaikan
        }

        let replyTo = null;
        if (!isMedia && parsedMessageData && typeof parsedMessageData === 'object') {
          if (parsedMessageData.user_reply_id) {
            replyTo = {
              messageID: parsedMessageData.message_id_reply,
              senderUserID: parsedMessageData.user_reply_id,
              message: parsedMessageData.value_reply,
              type_reply: parsedMessageData.type_reply || 'text',
              isDoctor: parsedMessageData.user_reply_id === userDokter,
            };
          }
        }

        newMessages.push({
          messageID: msg.messageID,
          message: isMedia ? '' : msg.message,
          timestamp: new Date(msg.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }),
          isDoctor: msg.senderUserID === userDokter,
          imageUrl: isMedia ? imageUrl : undefined,
          fileName: isMedia ? fileName : undefined,
          type: isMedia ? 'media' : 'text',
          replyTo: replyTo,
        });
      }

      if (newMessages.length > 0) {
        setMessages((prev) => {
          const merged = [...newMessages, ...prev];
          saveMessagesToLocalStorage(merged);
          return merged;
        });
        setMessageIds(newIds);
      }

      setNextMessage(history.nextMessage);
      setHasMore(fetchedMessages.length > 0);

    } catch (err) {
      console.error('Failed to load chat history:', err);
    } finally {
      setLoading(false);
    }
  };

  // Use ref to call loadChatHistory to avoid stale closure issues
  const loadChatHistoryRef = useRef(loadChatHistory);
  useEffect(() => {
    loadChatHistoryRef.current = loadChatHistory;
  }, [loadChatHistory]);

  useEffect(() => {
    if (isLoggedIn && userDokter) {
      // ✅ FIX: Wait a moment for receiveMessage to deliver offline messages first
      // Then only load history if we don't have messages yet
      const timer = setTimeout(() => {
        if (messagesRef.current.length === 0) {
          console.log('📚 No messages from receiveMessage, loading history from ZIM...');
          loadChatHistoryRef.current(null);
        } else {
          console.log(`📚 Skipping history load - already have ${messagesRef.current.length} messages from receiveMessage`);
        }
      }, 500); // Wait 500ms for receiveMessage to process

      return () => clearTimeout(timer);
    }
  }, [isLoggedIn, userDokter]);

  useEffect(() => {
    if (!teleconsultationData?.teleconsultation_expired_at) return;

    if (teleconsultationData?.teleconsultation_end_time && !teleconsultationData?.teleconsultation_isRating) {
      setShowRating(true);
    }
    // Dari Server kirim Y-m-d H:i:s GMT+7, jadi disini harus di parse sesuai itu
    var expiredTime = teleconsultationData.teleconsultation_expired_at
      ? Date.parse(teleconsultationData.teleconsultation_expired_at.replace(" ", "T") + "+07:00")
      : null;

    // ✅ FIX: Add +07:00 timezone to end_time parsing (was missing, causing 7-hour offset)
    const endTime = teleconsultationData.teleconsultation_end_time
      ? Date.parse(teleconsultationData.teleconsultation_end_time.replace(" ", "T") + "+07:00")
      : null;

    // ✅ FIX: Add null check for server_time to prevent NaN issues on initial load
    const serverTime = teleconsultationData.server_time;
    const timeOffset = serverTime
      ? new Date(serverTime).getTime() - Date.now()
      : 0; // Default to 0 offset if server_time is not available
    const now = Date.now() + timeOffset;

    // ✅ FIX: Validate expiredTime is a valid number
    if (expiredTime === null || isNaN(expiredTime)) {
      expiredTime = Date.now() + 10 * 60 * 1000;
    }

    // Jika sudah diakhiri secara manual
    // ✅ FIX: Add isNaN check AND positive value check to prevent false positives on initial load
    // endTime must be a valid positive timestamp to be considered
    if (endTime && !isNaN(endTime) && endTime > 0 && now > endTime) {
      setIsExpired(true);
      return;
    }

    // Jika sudah melewati waktu expired
    // ✅ FIX: Also validate expiredTime is a valid positive timestamp
    if (!isNaN(now) && expiredTime > 0 && now > expiredTime) {
      setIsExpired(true);
      return;
    }

    // Set timeout untuk expired
    const timeoutDuration = expiredTime - now;
    const expiredTimeout = setTimeout(() => {
      setIsExpired(true);
    }, timeoutDuration);

    // Set timeout untuk reminder 5 menit sebelum expired
    const reminderTime = expiredTime - 5 * 60 * 1000;
    const delayReminder = reminderTime - now;

    let reminderTimeout: NodeJS.Timeout | null = null;
    if (delayReminder <= 0) {
      // Kalau sudah masuk 5 menit terakhir
      setShowReminder(true);
    } else {
      // Pasang timer untuk show reminder
      reminderTimeout = setTimeout(() => {
        setShowReminder(true);
      }, delayReminder);
    }

    if (teleconsultationData.teleconsultation_end_time) {
      setShowRating(true);
      console.log('show rating');
    }


    return () => {
      clearTimeout(expiredTimeout);
      if (reminderTimeout) clearTimeout(reminderTimeout);
    };
  }, [teleconsultationData]);


  // Retry handlers for error states
  const handleRetryLogin = useCallback(() => {
    setLoginRetryCount(0);
    loginStartedRef.current = false;
    setLoginError(null);
  }, []);

  const handleRetryData = useCallback(() => {
    fetchTeleconsultationData();
  }, [fetchTeleconsultationData]);

  // Show error state with retry button for login failures
  if (loginError) {
    return (
      <div style={{
        padding: 32,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 16
      }}>
        <div style={{ fontSize: 48 }}>⚠️</div>
        <div style={{ color: '#666', marginBottom: 8 }}>{loginError}</div>
        <button
          onClick={handleRetryLogin}
          style={{
            padding: '12px 24px',
            backgroundColor: '#007AFF',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            cursor: 'pointer'
          }}
        >
          Coba Lagi
        </button>
      </div>
    );
  }

  // Show error state with retry button for data fetch failures
  if (dataError && !teleconsultationData) {
    return (
      <div style={{
        padding: 32,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 16
      }}>
        <div style={{ fontSize: 48 }}>⚠️</div>
        <div style={{ color: '#666', marginBottom: 8 }}>{dataError}</div>
        <button
          onClick={handleRetryData}
          style={{
            padding: '12px 24px',
            backgroundColor: '#007AFF',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            cursor: 'pointer'
          }}
        >
          Coba Lagi
        </button>
      </div>
    );
  }

  if (!teleconsultationData) {
    return (
      <>
        {/* <HeaderWithBack title="Chat" /> */}
        <div style={{
          padding: 16,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 12
        }}>
          <div className="loading-spinner" style={{
            width: 40,
            height: 40,
            border: '3px solid #f3f3f3',
            borderTop: '3px solid #007AFF',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <div>Memuat data telekonsultasi...</div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </>
    );
  }

  // export function sendMessageToReactNativeApp(
  //   { action, payload },
  //   others = {},
  // ) {
  //   if (!action || typeof action !== 'string') {
  //   throw new Error('Action must be a non-empty string');
  //   }

  //   const message = { action, ...(payload && { payload }), ...others };

  //   if (typeof window.ReactNativeWebView?.postMessage === 'function') {
  //   window.ReactNativeWebView.postMessage(JSON.stringify(message));
  //   } else {
  //   // console.warn('window.ReactNativeWebView.postMessage is not a function');
  //   }
  // }



  return (
    <>
      {/* <HeaderWithBack title="Chat" url='/home' /> */}
      <ChatContainer>
        <ChatHeader
          doctorAvatar={teleconsultationData?.practitioner_avatar_url || 'https://m.linksehat.dev/assets/img/users/female-avatar.png'}
          doctorName={teleconsultationData?.practitioner_name || 'Dokter'}
          doctorSpecialty={teleconsultationData?.practitioner_speciality || 'Spesialis Umum'}
          consultationMedia={teleconsultationData?.teleconsultation_type || 'CHAT'}
          startTime={teleconsultationData?.teleconsultation_start_time} // default 1 jam
          duration={teleconsultationData?.teleconsultation_duration_timeout} // default 1 jam
          isExpired={isExpired}
          consultationId={teleconsultationData?.teleconsultation_id || ''} // ID telekonsultasi
          token={decryptedToken} // Token untuk API
          serverTime={teleconsultationData?.server_time}
        />

        {/* Centered PatientCard */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0', boxShadow: '2px 2px 2px rgba(0,0,0,0.1)', zIndex: 100 }}>
          <PatientCard
            name={teleconsultationData?.patient_name || 'Nama Pasien'}
            insurance={teleconsultationData?.patient_insurance || 'Insurance'}
          />
        </div>

        <MessagesContainer ref={messagesContainerRef}>
          {messages.filter((msg) =>
            typeof msg.message === 'string' &&
            // !msg.message.includes('accepted|') &&
            !msg.message.includes('rejected|') &&
            !msg.message.includes('cancelled|') &&
            !msg.message.includes('rescheduled|') &&
            !msg.message.includes('completed|') &&
            !msg.message.includes('expired|')
            // !msg.message.includes('end|')
          ).map((msg, index) => (
            <ChatMessage
              key={`${msg.messageID}-${msg.timestamp}`}
              {...msg}
              onReply={() => setReplyTo(msg)}
              doctorName={teleconsultationData?.practitioner_name || 'Dokter'}
              doctorSpecialty={teleconsultationData?.practitioner_speciality || 'Spesialis Umum'}
              avatar={teleconsultationData?.practitioner_avatar_url}
              userDokter={userDokter}
              patientName={teleconsultationData?.patient_name}
              ref={(element: any) => {
                if (element) messageRefsMap.current[msg.messageID] = element;
              }}
              isHighlighted={highlightedMessageID === msg.messageID}
              onReplyQuoteClick={handleReplyQuoteClick}
              replyTo={msg.replyTo}
            />
          ))}
          <div ref={messagesEndRef} />
        </MessagesContainer>
        {!isExpired && (
          <ChatInputContainer ref={chatInputRef} style={{ bottom: keyboardOffset }}>
            <ChatInput
              onSend={handleSend}
              onSendMedia={handleSendMedia}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              doctorName={teleconsultationData?.practitioner_name || 'Dokter'}
              isDoctor={false}
            />
          </ChatInputContainer>
        )}

        <ConsultationReminderModal
          visible={showReminder}
          onClose={() => setShowReminder(false)}
        />

        <FeedbackScreen
          doctorId={teleconsultationData?.practitioner_id || ''}
          teleconsultId={teleconsultationData?.teleconsultation_id || ''}
          teleconsultationCode={teleconsultationData?.teleconsultation_code || ''}
          patientId={teleconsultationData?.patient_id || ''}
          token={decryptedToken}
          visible={showRating}
          onClose={() => setShowRating(false)}
        />
      </ChatContainer>
    </>
  );
};

export default Chat;
